/** @format */

import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { create } from 'jss';
import rtl from 'jss-rtl';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider } from 'notistack';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { jssPreset, StylesProvider } from '@mui/styles';
import ScrollReset from 'src/components/ScrollReset';
import useSettings from 'src/hooks/useSettings';
import { createContiTheme } from 'src/theme';
import routes, { renderRoutes } from 'src/routes';
import Ga4PageViewTracker from './components/Ga4PageViewTracker';
import { markLanguageEventTracked } from 'src/analytics/languageDataLayer';

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

window.addEventListener('unhandledrejection', event => {
  // Suppress CKEditor watchdog errors specifically
  const reason = event.reason as { message?: string } | undefined;
  if (reason?.message?.includes('Cannot read properties of null')) {
    console.warn('Suppressed CKEditor error:', reason.message);
    event.preventDefault();
  }
});

const App = () => {
  const { settings } = useSettings();

  const theme = createContiTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme
  });

  /**
   * If the url contains a language parameter, remove it and store the chosen language in the dataLayer.
   * Marks language as tracked so dashboard does not also push a `language` event.
   */
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('language') && window.trackDataLayer) {
      const lang = url.searchParams.get('language');
      window.trackDataLayer('language_changed', { language: lang });
      markLanguageEventTracked();
      url.searchParams.delete('language');
      const newUrl = url.pathname + url.search + url.hash;
      window.history.replaceState(null, document.title, newUrl);
    }
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <StylesProvider jss={jss}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <SnackbarProvider
              dense
              maxSnack={3}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}>
              <BrowserRouter>
                <ScrollReset />
                <Ga4PageViewTracker />
                {renderRoutes(routes)}
              </BrowserRouter>
            </SnackbarProvider>
          </LocalizationProvider>
        </StylesProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
